import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../../services/api.js';
import { ProductSelectorMulti } from './ProductSelectorMulti.js';
import { PlacementCommitmentsForm, PlacementCommitments } from './PlacementCommitmentsForm.js';
import { OrderCollectionForm, OrderItem } from './OrderCollectionForm.js';
import { FollowupActionsForm } from './FollowupActionsForm.js';
import { VisitNotesSection } from './VisitNotesSection.js';
import { InlinePoSForm } from './InlinePoSForm.js';
import { OpenTasksBanner } from './OpenTasksBanner.js';
import { VisitBriefingCard } from './VisitBriefingCard.js';
import { FollowupEmailOptIn } from './FollowupEmailOptIn.js';
import { FridgeInterestButton } from './FridgeInterestButton.js';
import { useOpenTasks } from './useOpenTasks.js';
import { Product, PoS, Distributor } from '../../types/index.js';
import { useProducts, useDistributors } from '../../hooks/useOfflineData.js';
import { useOnlineStatus } from '../../hooks/useOnlineStatus.js';
import { addToSubmissionQueue } from '../../services/offlineStorage.js';
import { useAuth } from '../../contexts/AuthContext.js';
import { formatDocumentToast } from '../../utils/documentToast';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import {
  clearActiveVisitDraft,
  isMeaningfulDraft,
  loadActiveVisitDraft,
  saveActiveVisitDraft,
  type NewPosDistributorDraft,
  type NewPosFormDraft,
  type VisitWizardDraft,
} from '../../utils/activeVisitDraft';
import {
  toggleSeriousInterest,
  type FollowupPriority,
  type InterestBaseline,
} from '../../utils/followupPriority';
import {
  Search,
  Plus,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from 'lucide-react';

interface IntroductionDetails {
  samples_quantity: number;
  presentation_type: 'full_presentation' | 'left_samples_only';
  materials_left: string[];
  decision_maker_present: boolean;
  interest_level: 'hot' | 'warm' | 'cold';
}

interface ProductPlacementData {
  placements: PlacementCommitments;
  is_new_introduction: boolean;
  introduction?: IntroductionDetails;
  secured: boolean;
}

const MATERIALS_OPTIONS = [
  { value: 'flyers', label: 'Flyers' },
  { value: 'price_list', label: 'Price List' },
  { value: 'pos_display', label: 'POS Display' },
];

/** Default samples left = 1 per active variant (Noda 4, Raaaw 3, …). */
function defaultSamplesQuantity(product: Product | undefined): number {
  const count = Number(product?.active_variant_count);
  if (Number.isFinite(count) && count > 0) return Math.floor(count);
  return 1;
}

export const VisitWizard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { products } = useProducts();
  const { distributors } = useDistributors();
  const { isOnline } = useOnlineStatus();
  const { user, team } = useAuth();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Pre-step: PoS selection
  const preselectedPosId = searchParams.get('posId');
  const [posMode, setPosMode] = useState<'existing' | 'new'>(
    preselectedPosId ? 'existing' : 'existing'
  );
  const [selectedPos, setSelectedPos] = useState<PoS | null>(null);
  const [posSearch, setPosSearch] = useState('');
  const [posResults, setPosResults] = useState<PoS[]>([]);
  const [posSearching, setPosSearching] = useState(false);
  const [isNewPos, setIsNewPos] = useState(false);
  const [newPosForm, setNewPosForm] = useState<NewPosFormDraft | null>(null);
  const [newPosDistributors, setNewPosDistributors] = useState<NewPosDistributorDraft[]>([]);
  const [posFormEpoch, setPosFormEpoch] = useState(0);

  // Open tasks of the selected PoS, resolved or updated together with this visit
  const openTasks = useOpenTasks(
    isNewPos ? null : selectedPos?.id,
    searchParams.get('resolveFollowupId')
  );


  // Step 1: Products & Placements
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [productPlacements, setProductPlacements] = useState<
    Record<string, ProductPlacementData>
  >({});
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);

  // Step 2: Orders, Performance & Wrap-up
  const [salesExpanded, setSalesExpanded] = useState(false);
  const [salesPerformance, setSalesPerformance] = useState<
    Record<string, { units_sold: number; current_stock: number; rating: string; notes: string }>
  >({});
  const [otherOutcome, setOtherOutcome] = useState('');
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [ordersExpanded, setOrdersExpanded] = useState(false);
  const [followupExpanded, setFollowupExpanded] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [visitNotes, setVisitNotes] = useState('');
  const [notesPinned, setNotesPinned] = useState(false);
  const [sendFollowupEmail, setSendFollowupEmail] = useState(false);
  const [followupEmailRecipient, setFollowupEmailRecipient] = useState('');
  const [followupEmailLanguage, setFollowupEmailLanguage] = useState<'de' | 'en'>('de');
  const [needsFollowup, setNeedsFollowup] = useState(false);
  const [followupMaterials, setFollowupMaterials] = useState<string[]>([]);
  const [followupActions, setFollowupActions] = useState<string[]>([]);
  const [followupNotes, setFollowupNotes] = useState('');
  const [followupPriority, setFollowupPriority] = useState<FollowupPriority>('medium');
  const [interestBaseline, setInterestBaseline] = useState<InterestBaseline | null>(null);
  const hydrated = useRef(false);
  const skipPersist = useRef(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [conflictDraft, setConflictDraft] = useState<VisitWizardDraft | null>(null);

  const applyDraft = (draft: VisitWizardDraft) => {
    setStep(draft.step);
    setPosMode(draft.posMode);
    setSelectedPos(draft.selectedPos);
    setIsNewPos(draft.isNewPos);
    setSelectedProductIds(draft.selectedProductIds);
    setProductPlacements(
      draft.productPlacements as Record<string, ProductPlacementData>
    );
    setExpandedProduct(draft.expandedProduct);
    setSalesExpanded(draft.salesExpanded);
    setSalesPerformance(draft.salesPerformance);
    setOtherOutcome(draft.otherOutcome);
    setOrders(draft.orders);
    setOrdersExpanded(draft.ordersExpanded);
    setFollowupExpanded(draft.followupExpanded);
    setNotesExpanded(draft.notesExpanded);
    setVisitNotes(draft.visitNotes);
    setNotesPinned(draft.notesPinned);
    setSendFollowupEmail(draft.sendFollowupEmail);
    setFollowupEmailRecipient(draft.followupEmailRecipient);
    setFollowupEmailLanguage(draft.followupEmailLanguage ?? 'de');
    setNeedsFollowup(draft.needsFollowup);
    setFollowupMaterials(draft.followupMaterials);
    setFollowupActions(draft.followupActions);
    setFollowupNotes(draft.followupNotes);
    setFollowupPriority(draft.followupPriority);
    setInterestBaseline(draft.interestBaseline ?? null);
    setNewPosForm(draft.newPosForm ?? null);
    setNewPosDistributors(draft.newPosDistributors ?? []);
    setPosFormEpoch((epoch) => epoch + 1);
  };

  const currentDraft = (): VisitWizardDraft => ({
    version: 1,
    step,
    posMode,
    selectedPos,
    isNewPos,
    selectedProductIds,
    productPlacements,
    expandedProduct,
    salesExpanded,
    salesPerformance,
    otherOutcome,
    orders,
    ordersExpanded,
    followupExpanded,
    notesExpanded,
    visitNotes,
    notesPinned,
    sendFollowupEmail,
    followupEmailRecipient,
    followupEmailLanguage,
    needsFollowup,
    followupMaterials,
    followupActions,
    followupNotes,
    followupPriority,
    interestBaseline,
    newPosForm,
    newPosDistributors,
  });

  useEffect(() => {
    if (!user?.id || hydrated.current) return;
    hydrated.current = true;
    const draft = loadActiveVisitDraft(user.id);
    if (draft && isMeaningfulDraft(draft)) {
      if (
        preselectedPosId &&
        draft.selectedPos?.id &&
        draft.selectedPos.id !== preselectedPosId
      ) {
        setConflictDraft(draft);
        return;
      }
      applyDraft(draft);
      return;
    }
    if (preselectedPosId) {
      api.get(`/pos/${preselectedPosId}`).then((res) => {
        setSelectedPos(res.data);
        setIsNewPos(false);
      }).catch(() => {});
    }
  }, [user?.id, preselectedPosId]);

  const cancelStartedVisit = () => {
    skipPersist.current = true;
    if (user?.id) clearActiveVisitDraft(user.id);
    setStep(0);
    setSelectedPos(null);
    setIsNewPos(false);
    setPosSearch('');
    setPosResults([]);
    setSelectedProductIds([]);
    setProductPlacements({});
    setOrders([]);
    setVisitNotes('');
    setNeedsFollowup(false);
    setNewPosForm(null);
    setNewPosDistributors([]);
    setPosFormEpoch((epoch) => epoch + 1);
  };

  useEffect(() => {
    if (!user?.id || !hydrated.current || conflictDraft) return;
    if (skipPersist.current) {
      skipPersist.current = false;
      return;
    }
    const timer = setTimeout(() => {
      if (skipPersist.current) return;
      const draft = currentDraft();
      if (isMeaningfulDraft(draft)) saveActiveVisitDraft(user.id, draft);
      else clearActiveVisitDraft(user.id);
    }, 300);
    return () => clearTimeout(timer);
    // Persist whenever wizard fields change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.id,
    conflictDraft,
    step,
    posMode,
    selectedPos,
    isNewPos,
    selectedProductIds,
    productPlacements,
    expandedProduct,
    salesExpanded,
    salesPerformance,
    otherOutcome,
    orders,
    ordersExpanded,
    followupExpanded,
    notesExpanded,
    visitNotes,
    notesPinned,
    sendFollowupEmail,
    followupEmailRecipient,
    followupEmailLanguage,
    needsFollowup,
    followupMaterials,
    followupActions,
    followupNotes,
    followupPriority,
    interestBaseline,
    newPosForm,
    newPosDistributors,
  ]);

  const searchPoS = useCallback(async (query: string) => {
    if (query.length < 2) {
      setPosResults([]);
      return;
    }
    setPosSearching(true);
    try {
      const res = await api.get(`/pos?search=${encodeURIComponent(query)}&limit=10`);
      setPosResults(res.data.data || res.data || []);
    } catch {
      setPosResults([]);
    } finally {
      setPosSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchPoS(posSearch), 300);
    return () => clearTimeout(timer);
  }, [posSearch, searchPoS]);

  const handleSelectPos = (pos: PoS) => {
    setSelectedPos(pos);
    setIsNewPos(false);
    setPosSearch('');
    setPosResults([]);
    // Do not auto-advance: open tasks are shown on this step first.
  };

  const handleNewPosCreated = (pos: PoS) => {
    setSelectedPos(pos);
    setIsNewPos(true);
    setNewPosForm(null);
    setNewPosDistributors([]);
    setStep(1);
  };

  const onNewPosDraft = useCallback(
    (values: NewPosFormDraft, distributors: NewPosDistributorDraft[]) => {
      setNewPosForm(values);
      setNewPosDistributors(distributors);
    },
    []
  );

  const initPlacementForProduct = (productId: string): ProductPlacementData => ({
    placements: {
      general_shelf: false,
      fridge_placement: false,
      fridge_facings_count: 0,
      dry_shelf_placement: false,
      dry_shelf_facings_count: 0,
      poster_placement: false,
      display_stand: false,
      checkout_placement: false,
    },
    is_new_introduction: false,
    secured: false,
  });

  const setProductSecured = (productId: string, secured: boolean) => {
    setProductPlacements((prev) => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || initPlacementForProduct(productId)),
        secured,
      },
    }));
  };

  const anyProductSecured = (): boolean =>
    selectedProductIds.some((id) => productPlacements[id]?.secured === true);

  const productsWithOrderButNotSecured = (): string[] => {
    const orderedIds = new Set(orders.map((o) => o.product_id));
    return selectedProductIds.filter(
      (id) => orderedIds.has(id) && !productPlacements[id]?.secured
    );
  };

  useEffect(() => {
    setProductPlacements((prev) => {
      const updated = { ...prev };
      selectedProductIds.forEach((id) => {
        if (!updated[id]) updated[id] = initPlacementForProduct(id);
      });
      Object.keys(updated).forEach((id) => {
        if (!selectedProductIds.includes(id)) delete updated[id];
      });
      return updated;
    });
  }, [selectedProductIds]);

  const updatePlacement = (productId: string, placements: PlacementCommitments) => {
    setProductPlacements((prev) => ({
      ...prev,
      [productId]: { ...prev[productId], placements },
    }));
  };

  const toggleIntroduction = (productId: string) => {
    setProductPlacements((prev) => {
      const current = prev[productId];
      const isNew = !current.is_new_introduction;
      const product = products.find((p) => p.id === productId);
      return {
        ...prev,
        [productId]: {
          ...current,
          is_new_introduction: isNew,
          introduction: isNew
            ? current.introduction || {
                samples_quantity: defaultSamplesQuantity(product),
                presentation_type: 'full_presentation' as const,
                materials_left: [],
                decision_maker_present: false,
                interest_level: 'warm' as const,
              }
            : undefined,
        },
      };
    });
  };

  const updateIntroduction = (
    productId: string,
    field: keyof IntroductionDetails,
    value: any
  ) => {
    setProductPlacements((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        introduction: { ...prev[productId].introduction!, [field]: value },
      },
    }));
  };

  const canProceed = (): boolean => {
    if (step === 0) return selectedPos !== null;
    if (step === 1) return selectedProductIds.length > 0;
    return true;
  };

  const hasOrdersOrPlacements = (): boolean => {
    if (orders.length > 0) return true;
    return Object.values(productPlacements).some((entry) => {
      if (!entry?.placements) return false;
      return Object.values(entry.placements).some(
        (v) => v === true || (typeof v === 'number' && v > 0)
      );
    });
  };

  const handleSubmit = async () => {
    if (!selectedPos) return;
    setLoading(true);

    try {
      const visitOutcome = anyProductSecured()
        ? 'placement_secured'
        : otherOutcome || undefined;

      const payload: any = {
        pos_id: selectedPos.id,
        store_type: selectedPos.store_type,
        visit_type: isNewPos ? 'first_time' : 'revisit',
        visit_products: selectedProductIds,
        product_placements: productPlacements,
        visit_outcome: visitOutcome,
        sales_performance:
          Object.keys(salesPerformance).length > 0 ? salesPerformance : undefined,
        notes: visitNotes || undefined,
        ...(visitNotes.trim() !== '' ? { notes_pinned: notesPinned } : {}),
        needs_followup: needsFollowup,
        followup_materials: needsFollowup ? followupMaterials : undefined,
        followup_actions: needsFollowup ? followupActions : undefined,
        followup_notes: needsFollowup ? followupNotes : undefined,
        followup_priority: needsFollowup ? followupPriority : undefined,
        resolves_visit_ids:
          openTasks.resolveIds.length > 0 ? openTasks.resolveIds : undefined,
        followup_updates:
          openTasks.updates.length > 0 ? openTasks.updates : undefined,
        send_followup_email: sendFollowupEmail || undefined,
        followup_email_recipient:
          sendFollowupEmail && followupEmailRecipient.trim()
            ? followupEmailRecipient.trim()
            : undefined,
        followup_email_language: sendFollowupEmail ? followupEmailLanguage : undefined,
        orders:
          orders.length > 0
            ? orders.map((o) => ({
                product_id: o.product_id,
                variant_id: o.variant_id || undefined,
                quantity: o.quantity,
                promotion_type: o.promotion_type,
                distributor_id: o.distributor_id || undefined,
                ...(o.unit_price_override !== undefined
                  ? { unit_price_override: o.unit_price_override }
                  : {}),
              }))
            : undefined,
      };

      if (isOnline) {
        const res = await api.post('/visits', payload);
        const docs = res.data?.documents || [];
        toast.success(
          docs.length > 0
            ? `Visit saved · ${formatDocumentToast(docs)}`
            : 'Visit saved'
        );
      } else {
        await addToSubmissionQueue('visit', {
          ...payload,
          _offline_context: { user_id: user?.id, team_id: team?.id },
        });
        toast.success('Saved offline — will sync when online');
      }
      if (user?.id) clearActiveVisitDraft(user.id);
      navigate('/visits');
    } catch (err: any) {
      const payload = err.response?.data?.error;
      const message =
        typeof payload === 'string'
          ? payload
          : payload?.message || 'Failed to save visit';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const stepLabels = ['PoS', 'Products', 'Order lines & wrap-up'];
  // In new-PoS mode the inline form is the only way forward.
  const showNext = step < 2 && !(step === 0 && posMode === 'new');

  return (
    <div className="max-w-3xl mx-auto p-4">
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <div className="flex justify-between text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest flex-1 gap-2">
          {stepLabels.map((label, i) => (
            <span key={label} className={step >= i ? 'text-brand-600' : ''}>
              {label}
            </span>
          ))}
          </div>
          <button
            type="button"
            onClick={() => setDiscardOpen(true)}
            className="ml-3 min-h-[36px] px-2.5 rounded-xl border border-gray-200 text-xs font-bold text-gray-500"
          >
            Discard
          </button>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-500 transition-all duration-500 ease-out"
            style={{ width: `${((step + 1) / stepLabels.length) * 100}%` }}
          />
        </div>
      </div>

      {/* STEP 0: PoS Selection */}
      {step === 0 && (
        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
          <h2 className="text-2xl font-black text-gray-900">Select Point of Sale</h2>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setPosMode('existing')}
              className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${
                posMode === 'existing'
                  ? 'bg-brand-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Existing PoS
            </button>
            <button
              type="button"
              onClick={() => setPosMode('new')}
              className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${
                posMode === 'new'
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Plus size={16} className="inline mr-1" />
              New PoS
            </button>
          </div>

          {posMode === 'existing' && (
            <div className="space-y-3">
              <div className="relative">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={posSearch}
                  onChange={(e) => setPosSearch(e.target.value)}
                  placeholder="Search by name or address..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none"
                />
              </div>
              {posSearching && (
                <p className="text-sm text-gray-500">Searching...</p>
              )}
              {posResults.length > 0 && (
                <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
                  {posResults.map((pos) => (
                    <li
                      key={pos.id}
                      onClick={() => handleSelectPos(pos)}
                      className="px-4 py-3 hover:bg-brand-50 cursor-pointer transition-colors"
                    >
                      <p className="font-semibold text-gray-900">{pos.name}</p>
                      <p className="text-sm text-gray-500">
                        {pos.street} {pos.street_number}, {pos.postal_code}{' '}
                        {pos.city}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {selectedPos && (
                <VisitBriefingCard
                  pos={selectedPos}
                  products={products}
                  onPosUpdated={setSelectedPos}
                />
              )}

              {selectedPos && (
                <OpenTasksBanner
                  tasks={openTasks.tasks}
                  loading={openTasks.loading}
                  decisions={openTasks.decisions}
                  onChange={openTasks.setDecision}
                  variant="compact"
                />
              )}
            </div>
          )}

          {posMode === 'new' && (
            <InlinePoSForm
              key={posFormEpoch}
              initialValues={newPosForm}
              initialDistributors={newPosDistributors}
              onDraftChange={onNewPosDraft}
              onCreated={handleNewPosCreated}
            />
          )}
        </div>
      )}

      {/* STEP 1: Products & Placements */}
      {step === 1 && (
        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
          <h2 className="text-2xl font-black text-gray-900">Products & Placements</h2>

          <ProductSelectorMulti
            value={selectedProductIds}
            onChange={setSelectedProductIds}
            allowMultiple={true}
          />

          {selectedProductIds.length > 0 && (
            <div className="space-y-4 mt-6">
              {selectedProductIds.map((pid) => {
                const product = products.find((p) => p.id === pid);
                const pData = productPlacements[pid];
                if (!product || !pData) return null;
                const isExpanded = expandedProduct === pid;

                return (
                  <div
                    key={pid}
                    className="border border-gray-200 rounded-xl overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedProduct(isExpanded ? null : pid)
                      }
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <span className="font-bold text-gray-900">
                        {product.name}
                      </span>
                      {isExpanded ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="p-4 space-y-4">
                        <PlacementCommitmentsForm
                          value={pData.placements}
                          onChange={(v) => updatePlacement(pid, v)}
                        />

                        {/* New Introduction Toggle */}
                        <div className="border-t pt-4">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={pData.is_new_introduction}
                              onChange={() => toggleIntroduction(pid)}
                              className="h-5 w-5 text-brand-600 rounded border-gray-300"
                            />
                            <span className="font-medium text-gray-700 flex items-center gap-2">
                              <Sparkles size={16} className="text-amber-500" />
                              New Introduction
                            </span>
                          </label>
                        </div>

                        {/* Introduction Details */}
                        {pData.is_new_introduction && pData.introduction && (
                          <div className="ml-4 p-4 border border-amber-200 rounded-lg bg-amber-50 space-y-4">
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">
                                Samples Provided
                              </label>
                              <input
                                type="number"
                                min="0"
                                inputMode="numeric"
                                placeholder="0"
                                value={
                                  pData.introduction.samples_quantity === 0
                                    ? ''
                                    : pData.introduction.samples_quantity
                                }
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  updateIntroduction(
                                    pid,
                                    'samples_quantity',
                                    raw === '' ? 0 : parseInt(raw, 10) || 0
                                  );
                                }}
                                className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Presentation Type
                              </label>
                              <div className="flex gap-2">
                                {(
                                  [
                                    {
                                      value: 'full_presentation',
                                      label: 'Full Presentation',
                                    },
                                    {
                                      value: 'left_samples_only',
                                      label: 'Left Samples Only',
                                    },
                                  ] as const
                                ).map((opt) => (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() =>
                                      updateIntroduction(
                                        pid,
                                        'presentation_type',
                                        opt.value
                                      )
                                    }
                                    className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                                      pData.introduction!.presentation_type ===
                                      opt.value
                                        ? 'bg-amber-500 text-white'
                                        : 'bg-white border border-gray-300 text-gray-700'
                                    }`}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Materials Left
                              </label>
                              <div className="flex flex-wrap gap-2">
                                {MATERIALS_OPTIONS.map((mat) => {
                                  const selected =
                                    pData.introduction!.materials_left.includes(
                                      mat.value
                                    );
                                  return (
                                    <button
                                      key={mat.value}
                                      type="button"
                                      onClick={() => {
                                        const current =
                                          pData.introduction!.materials_left;
                                        updateIntroduction(
                                          pid,
                                          'materials_left',
                                          selected
                                            ? current.filter(
                                                (m) => m !== mat.value
                                              )
                                            : [...current, mat.value]
                                        );
                                      }}
                                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                        selected
                                          ? 'bg-amber-500 text-white'
                                          : 'bg-white border border-gray-300 text-gray-700'
                                      }`}
                                    >
                                      {mat.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              <label className="text-sm font-semibold text-gray-700">
                                Decision Maker Present
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  updateIntroduction(
                                    pid,
                                    'decision_maker_present',
                                    !pData.introduction!
                                      .decision_maker_present
                                  )
                                }
                                className={`px-4 py-1.5 rounded-lg text-sm font-bold transition-all ${
                                  pData.introduction!
                                    .decision_maker_present
                                    ? 'bg-green-500 text-white'
                                    : 'bg-gray-200 text-gray-600'
                                }`}
                              >
                                {pData.introduction!
                                  .decision_maker_present
                                  ? 'Yes'
                                  : 'No'}
                              </button>
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Interest Level
                              </label>
                              <div className="flex gap-2">
                                {(
                                  [
                                    {
                                      value: 'hot',
                                      label: 'Hot',
                                      color: 'bg-red-500',
                                    },
                                    {
                                      value: 'warm',
                                      label: 'Warm',
                                      color: 'bg-amber-500',
                                    },
                                    {
                                      value: 'cold',
                                      label: 'Cold',
                                      color: 'bg-blue-500',
                                    },
                                  ] as const
                                ).map((opt) => (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() =>
                                      updateIntroduction(
                                        pid,
                                        'interest_level',
                                        opt.value
                                      )
                                    }
                                    className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
                                      pData.introduction!.interest_level ===
                                      opt.value
                                        ? `${opt.color} text-white`
                                        : 'bg-white border border-gray-300 text-gray-700'
                                    }`}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* STEP 2: Orders & Wrap-up */}
      {step === 2 && (
        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
          <h2 className="text-2xl font-black text-gray-900">
            Order lines & wrap-up
          </h2>

          <OpenTasksBanner
            tasks={openTasks.tasks}
            loading={openTasks.loading}
            decisions={openTasks.decisions}
            onChange={openTasks.setDecision}
            variant="full"
          />

          {/* Orders (expandable) */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setOrdersExpanded(!ordersExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span className="font-bold text-gray-900">
                Order lines{orders.length > 0 ? ` (${orders.length})` : ''}
              </span>
              {ordersExpanded ? (
                <ChevronUp size={20} />
              ) : (
                <ChevronDown size={20} />
              )}
            </button>
            {ordersExpanded && (
              <div className="p-4">
                <OrderCollectionForm
                  products={products}
                  distributors={distributors}
                  value={orders}
                  onChange={setOrders}
                  storeType={selectedPos?.store_type ?? newPosForm?.store_type ?? null}
                />
              </div>
            )}
          </div>

          {/* Follow-up (expandable) */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setFollowupExpanded(!followupExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span className="font-bold text-gray-900">Follow-up</span>
              {followupExpanded ? (
                <ChevronUp size={20} />
              ) : (
                <ChevronDown size={20} />
              )}
            </button>
            {followupExpanded && (
              <div className="p-4">
                <FollowupActionsForm
                  needsFollowup={needsFollowup}
                  onNeedsFollowupChange={setNeedsFollowup}
                  materials={followupMaterials}
                  onMaterialsChange={setFollowupMaterials}
                  actions={followupActions}
                  onActionsChange={setFollowupActions}
                  notes={followupNotes}
                  onNotesChange={setFollowupNotes}
                  priority={followupPriority}
                  onPriorityChange={setFollowupPriority}
                />
              </div>
            )}
          </div>

          {/* Sales Performance (expandable) */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setSalesExpanded(!salesExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span className="font-bold text-gray-900">
                Sales Performance (optional)
              </span>
              {salesExpanded ? (
                <ChevronUp size={20} />
              ) : (
                <ChevronDown size={20} />
              )}
            </button>
            {salesExpanded && (
              <div className="p-4 space-y-4">
                {selectedProductIds.map((pid) => {
                  const product = products.find((p) => p.id === pid);
                  if (!product) return null;
                  const sp = salesPerformance[pid] || {
                    units_sold: 0,
                    current_stock: 0,
                    rating: 'moderate',
                    notes: '',
                  };
                  return (
                    <div
                      key={pid}
                      className="p-3 border rounded-lg space-y-2"
                    >
                      <p className="font-semibold">{product.name}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500">
                            Units Sold
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={sp.units_sold}
                            onChange={(e) =>
                              setSalesPerformance((prev) => ({
                                ...prev,
                                [pid]: {
                                  ...sp,
                                  units_sold:
                                    parseInt(e.target.value) || 0,
                                },
                              }))
                            }
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">
                            Current Stock
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={sp.current_stock}
                            onChange={(e) =>
                              setSalesPerformance((prev) => ({
                                ...prev,
                                [pid]: {
                                  ...sp,
                                  current_stock:
                                    parseInt(e.target.value) || 0,
                                },
                              }))
                            }
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">
                          Rating
                        </label>
                        <div className="flex gap-2 mt-1">
                          {['good', 'moderate', 'poor'].map((r) => (
                            <button
                              key={r}
                              type="button"
                              onClick={() =>
                                setSalesPerformance((prev) => ({
                                  ...prev,
                                  [pid]: { ...sp, rating: r },
                                }))
                              }
                              className={`flex-1 py-1.5 rounded-lg text-sm font-bold ${
                                sp.rating === r
                                  ? r === 'good'
                                    ? 'bg-green-500 text-white'
                                    : r === 'moderate'
                                      ? 'bg-amber-500 text-white'
                                      : 'bg-red-500 text-white'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {r.charAt(0).toUpperCase() + r.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <VisitNotesSection
            expanded={notesExpanded}
            onExpandedChange={setNotesExpanded}
            notes={visitNotes}
            onNotesChange={setVisitNotes}
            notesPinned={notesPinned}
            onNotesPinnedChange={setNotesPinned}
          />

          <FridgeInterestButton
            productNames={selectedProductIds.flatMap((id) => {
              const product = products.find((item) => item.id === id);
              if (!product?.lead_priority) return [];
              return [product.name];
            })}
            pressed={needsFollowup && followupPriority === 'urgent'}
            onToggle={() => {
              const names = selectedProductIds.flatMap((id) => {
                const product = products.find((item) => item.id === id);
                if (!product?.lead_priority) return [];
                return [product.name];
              });
              const next = toggleSeriousInterest({
                pressed: needsFollowup && followupPriority === 'urgent',
                needsFollowup,
                priority: followupPriority,
                notes: followupNotes,
                productNames: names,
                baseline: interestBaseline,
              });
              setNeedsFollowup(next.needsFollowup);
              setFollowupPriority(next.priority);
              setFollowupNotes(next.notes);
              setInterestBaseline(next.baseline);
              if (next.pressed) setFollowupExpanded(true);
            }}
          />

          {/* Per-product placement secured — never automatic */}
          <div className="border border-gray-200 rounded-xl p-4 space-y-3">
            <label className="text-sm font-bold text-gray-700">
              Placement secured (per product)
            </label>
            <p className="text-xs text-gray-500">
              Check only products whose placement was actually secured on this visit.
            </p>
            <div className="space-y-2">
              {selectedProductIds.map((pid) => {
                const product = products.find((p) => p.id === pid);
                const label = product
                  ? `${product.name}${product.manufacturer_name ? ` (${product.manufacturer_name})` : ''}`
                  : pid;
                const secured = productPlacements[pid]?.secured === true;
                return (
                  <label
                    key={pid}
                    className="flex items-start gap-3 cursor-pointer select-none rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
                  >
                    <input
                      type="checkbox"
                      checked={secured}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setProductSecured(pid, checked);
                        if (checked) setOtherOutcome('');
                      }}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-gray-900">
                        {label}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        Placement secured for this product
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            {productsWithOrderButNotSecured().length > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                You recorded orders for{' '}
                {productsWithOrderButNotSecured()
                  .map((id) => {
                    const p = products.find((x) => x.id === id);
                    return p?.name || id;
                  })
                  .join(', ')}{' '}
                — confirm Placement secured if applicable.
              </p>
            )}
            {hasOrdersOrPlacements() &&
              !anyProductSecured() &&
              productsWithOrderButNotSecured().length === 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                You recorded placements — confirm Placement secured per product
                if applicable.
              </p>
            )}
            {!anyProductSecured() && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">
                  Other outcome (optional)
                </label>
                <select
                  value={otherOutcome}
                  onChange={(e) => setOtherOutcome(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">None (follow-up / unsuccessful from wrap-up)</option>
                  <option value="follow_up_needed">Follow-up Needed</option>
                  <option value="rejected">Rejected</option>
                  <option value="competitor_exclusive">Competitor Exclusive</option>
                </select>
              </div>
            )}
          </div>

          <FollowupEmailOptIn
            enabled={sendFollowupEmail}
            onChange={setSendFollowupEmail}
            posEmail={selectedPos?.contact_person_email || selectedPos?.email || null}
            recipientOverride={followupEmailRecipient}
            onRecipientOverrideChange={setFollowupEmailRecipient}
            language={followupEmailLanguage}
            onLanguageChange={setFollowupEmailLanguage}
          />
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-8">
        {step > 0 ? (
          <button
            type="button"
            onClick={() => setStep(step - 1)}
            className="px-6 py-3 border-2 border-gray-200 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Back
          </button>
        ) : (
          <div />
        )}

        {showNext ? (
          <button
            type="button"
            onClick={() => {
              if (canProceed()) setStep(step + 1);
              else toast.warning('Please complete the required fields');
            }}
            className="px-8 py-3 bg-brand-600 text-white rounded-xl text-sm font-black uppercase tracking-widest hover:bg-brand-700 shadow-lg shadow-brand-200 transition-all active:scale-95"
          >
            Next
          </button>
        ) : step === 2 ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="px-8 py-3 bg-green-600 text-white rounded-xl text-sm font-black uppercase tracking-widest hover:bg-green-700 shadow-lg shadow-green-200 transition-all active:scale-95 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Submit Visit'}
          </button>
        ) : (
          <div />
        )}
      </div>

      <ConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Discard visit?"
        message="The in-progress visit will be cleared. You can start a new one afterwards."
        confirmText="Discard"
        variant="danger"
        onConfirm={() => {
          cancelStartedVisit();
          navigate('/visits');
        }}
      />
      {conflictDraft ? (
        <div className="fixed inset-0 z-[1000] bg-black/60 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3">
            <p className="text-base font-black text-gray-900">Unfinished visit</p>
            <p className="text-sm text-gray-600">
              {conflictDraft.selectedPos?.name
                ? `Resume ${conflictDraft.selectedPos.name}, or start this PoS instead?`
                : 'Resume the unfinished visit, or start this PoS instead?'}
            </p>
            <button
              type="button"
              className="w-full min-h-[44px] rounded-xl bg-gray-900 text-white text-sm font-bold"
              onClick={() => {
                applyDraft(conflictDraft);
                setConflictDraft(null);
              }}
            >
              Resume
            </button>
            <button
              type="button"
              className="w-full min-h-[44px] rounded-xl border border-gray-200 text-sm font-bold text-gray-700"
              onClick={() => {
                if (user?.id) clearActiveVisitDraft(user.id);
                setConflictDraft(null);
                if (!preselectedPosId) return;
                api.get(`/pos/${preselectedPosId}`).then((res) => {
                  setSelectedPos(res.data);
                  setIsNewPos(false);
                }).catch(() => {});
              }}
            >
              Start this PoS
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
